import type { AppointmentStatus } from "@planerius/contracts"
import type { ReactNode } from "react"

import { cx } from "../lib/cx"
import { cardDetail, GRID_MIN_CARD } from "./grid-geometry"
import styles from "./journal-grid.module.css"
import type { JournalCard } from "./journal-model"
import { FLAG_GLYPHS, FLAG_TITLES } from "./journal-model"
import type { StatusBorder, StatusFill } from "./status-style"
import { statusVisual } from "./status-style"

/**
 * Карточка записи в сетке.
 *
 * Самый частый объект журнала: за смену администратор смотрит на него сотни раз и почти
 * всегда периферийным зрением, не читая. Отсюда два правила, которые видно в коде:
 *
 * 1. Содержимое отбрасывается СНИЗУ ВВЕРХ по мере нехватки высоты — имя клиента остаётся
 *    последним (ТЗ §2.3.1). Что именно поместилось, решает cardDetail по высоте,
 *    а не медиазапрос: высота зависит от длительности услуги и шага сетки.
 * 2. Статус различим без цвета. Стиль границы, заливка и значок в фиксированном углу —
 *    три канала; цвет добавляется четвёртым и в градациях серого просто исчезает,
 *    ничего за собой не унося.
 */

const BORDER_CLASS: Readonly<Record<StatusBorder, string>> = {
  solid: "borderSolid",
  dashed: "borderDashed",
  thick: "borderThick",
  "thick-dashed": "borderThickDashed",
}

const FILL_CLASS: Readonly<Record<StatusFill, string>> = {
  plain: "fillPlain",
  solid: "fillSolid",
  "hatch-sparse": "fillHatchSparse",
  "hatch-dense": "fillHatchDense",
}

const TINT_CLASS: Readonly<Record<AppointmentStatus, string>> = {
  pending: "tintPending",
  confirmed: "tintConfirmed",
  in_progress: "tintRunning",
  completed: "tintCompleted",
  cancelled: "tintCancelled",
  no_show: "tintNoShow",
}

const GLYPH_CLASS: Readonly<Record<AppointmentStatus, string>> = {
  pending: "glyphPending",
  confirmed: "glyphPending",
  in_progress: "glyphRunning",
  completed: "glyphCompleted",
  cancelled: "glyphCancelled",
  no_show: "glyphNoShow",
}

const LABEL_CLASS: Readonly<Record<AppointmentStatus, string>> = {
  pending: "labelPending",
  confirmed: "labelCompleted",
  in_progress: "labelRunning",
  completed: "labelCompleted",
  cancelled: "labelCancelled",
  no_show: "labelNoShow",
}

export function AppointmentCard(props: {
  readonly card: JournalCard
  readonly top: number
  readonly height: number
  /**
   * Открыть панель записи. Пусто — карточка не кликается: так журнал выглядит
   * в офлайне и в технических работах, где панель открывать нечем, а не «открывается
   * и ругается».
   */
  readonly onOpen?: ((cardId: string) => void) | undefined
  /** Панель этой записи открыта: карточка обведена, чтобы не потеряться в дне. */
  readonly selected?: boolean
}): ReactNode {
  const { card, height } = props
  const interactive = props.onOpen !== undefined
  const visual = statusVisual(card.status)
  const detail = cardDetail(height)
  const service =
    card.extraServices > 0 ? `${card.serviceName} + ещё ${card.extraServices}` : card.serviceName

  const className = cx(
    styles["card"],
    card.inPause ? styles["cardInPause"] : undefined,
    styles[BORDER_CLASS[visual.border]],
    styles[FILL_CLASS[visual.fill]],
    styles[TINT_CLASS[card.status]],
    visual.hasBar ? styles["cardWithBar"] : undefined,
    visual.faded ? styles["cardFaded"] : undefined,
    interactive ? styles["cardButton"] : undefined,
    props.selected === true ? styles["cardSelected"] : undefined,
  )
  const style = { top: `${props.top}px`, height: `${height}px` }
  const title = `${card.tooltip} · ${visual.name}`

  const content = (
    <>
      {visual.hasBar ? <span className={styles["cardBar"]} aria-hidden="true" /> : null}

      <div className={styles["cardTop"]}>
        {visual.glyph === "" ? null : (
          <span
            className={cx(styles["glyph"], styles[GLYPH_CLASS[card.status]])}
            aria-hidden="true"
          >
            {visual.glyph}
          </span>
        )}
        <span
          className={cx(
            styles["cardName"],
            visual.dimName ? styles["cardNameDim"] : undefined,
            visual.strikeName ? styles["cardNameStruck"] : undefined,
          )}
        >
          {card.clientName}
        </span>
        {/* Признаки прижаты вправо и уходят первыми: они помогают, но имя важнее. */}
        {height >= GRID_MIN_CARD && card.flags.length > 0 ? (
          <span className={styles["cardFlags"]}>
            {card.flags.map((flag) => (
              <span key={flag} className={styles["flag"]} title={FLAG_TITLES[flag]}>
                {FLAG_GLYPHS[flag]}
              </span>
            ))}
          </span>
        ) : null}
      </div>

      {detail === "name" ? null : (
        <div className={styles["cardTime"]}>
          <span className={styles["cardTimeValue"]}>{card.timeLabel}</span>
          {visual.label === "" ? null : (
            <span className={cx(styles["cardStatusLabel"], styles[LABEL_CLASS[card.status]])}>
              {visual.label}
            </span>
          )}
        </div>
      )}

      {detail === "service" ? <div className={styles["cardService"]}>{service}</div> : null}
    </>
  )

  /*
   * Кликабельная карточка — НАСТОЯЩАЯ кнопка, а не div с обработчиком. На ней держатся
   * Tab, Enter и пробел: администратор за стойкой работает с клавиатуры, а программа
   * чтения с экрана обязана назвать это действием, а не абзацем текста.
   *
   * Нажатие НЕ уходит в колонку: клик по карточке открывает панель, клик мимо —
   * форму записи, и путать их нельзя.
   */
  if (!interactive) {
    return (
      <div className={className} style={style} title={title}>
        {content}
      </div>
    )
  }

  return (
    <button
      type="button"
      className={className}
      style={style}
      title={title}
      aria-label={`${card.clientName}, ${card.timeLabel}, ${visual.name}`}
      /* Открытая запись помечена и для глаз (обводка), и для чтения с экрана. */
      aria-current={props.selected === true ? "true" : undefined}
      onClick={(event) => {
        event.stopPropagation()
        props.onOpen?.(card.id)
      }}
    >
      {content}
    </button>
  )
}
